import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast';
import PaymentFlowStatus from '../../components/PaymentFlowStatus';
import '../payment/PaymentReceipt.css';

const REFUND_METHOD_NAMES = {
  toss_card: '신용·체크카드 취소',
  toss_transfer: '계좌이체 반환',
  toss_virtual_account: '가상계좌 반환',
  toss_mobile_phone: '휴대폰 결제 취소',
  toss_easy_pay: '간편결제 취소',
  카드: '신용·체크카드 취소',
  계좌이체: '계좌이체 반환',
  휴대폰: '휴대폰 결제 취소',
  간편결제: '간편결제 취소',
};

const formatReceiptDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatReceiptDay = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getRefundMethodName = (method) =>
  REFUND_METHOD_NAMES[method] || method || '원래 결제 수단으로 환불';

const RefundReceipt = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { paymentKey } = useParams();
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [refundData, setRefundData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getApiBaseUrl = () => {
    const currentUrl = window.location.hostname;
    if (currentUrl.includes('localhost')) {
      return 'http://localhost:8000';
    } else if (currentUrl.includes('whatquiztest')) {
      return 'https://quiz-api-test-397277209875.us-central1.run.app';
    }
    return 'https://quiz-api-397277209875.us-central1.run.app';
  };

  useEffect(() => {
    let cancelled = false;

    if (!currentUser) {
      navigate('/login');
      return;
    }

    const fetchRefundData = async () => {
      try {
        if (!cancelled) setLoading(true);

        if (location.state?.refundData) {
          if (!cancelled) setRefundData(location.state.refundData);
          return;
        }

        if (!paymentKey) {
          throw new Error('환불 정보를 찾을 수 없습니다.');
        }

        const idToken = await currentUser.getIdToken(true);
        const response = await fetch(`${getApiBaseUrl()}/api/payment/status/${paymentKey}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
        });

        if (!response.ok) {
          throw new Error('환불 정보를 가져오는데 실패했습니다.');
        }

        const result = await response.json();
        const paymentRecord = result.data.subscription_info?.payment_record
          || result.data.payment_record
          || {};

        const tossStatus = result.data.status;
        const internalStatus = paymentRecord.status;
        if (
          !['CANCELED', 'PARTIAL_CANCELED'].includes(tossStatus)
          && !['refunded', 'partial_canceled'].includes(internalStatus)
        ) {
          throw new Error('환불된 결제가 아닙니다.');
        }

        if (!cancelled) {
          const cancels = result.data.cancels || [];
          const latestCancel = cancels[cancels.length - 1] || {};
          setRefundData({
            paymentKey,
            orderId: result.data.orderId || result.data.order_id,
            amount: result.data.amount,
            refundAmount: paymentRecord.refund_amount
              || latestCancel.cancelAmount
              || result.data.cancelAmount
              || result.data.cancel_amount
              || result.data.amount,
            retainedAmount: paymentRecord.retained_amount,
            refundType: paymentRecord.refund_type || 'full',
            refundCalculation: paymentRecord.refund_calculation || {},
            refundImpact: paymentRecord.refund_impact || {},
            refundReconciliationRequired: Boolean(
              paymentRecord.refund_reconciliation_required,
            ),
            refundedAt: latestCancel.canceledAt
              || result.data.canceledAt
              || result.data.canceled_at
              || paymentRecord.refunded_at,
            cancelReason: latestCancel.cancelReason
              || result.data.cancelReason
              || result.data.cancel_reason
              || '관리자 환불',
            originalPaymentDate: result.data.approvedAt
              || result.data.approved_at
              || paymentRecord.created_at,
            planName: paymentRecord.description || '왓퀴즈 PRO 이용권',
            receiptNumber: result.data.orderId || paymentKey,
            paymentMethod: result.data.method || paymentRecord.payment_method,
            receipt: result.data.receipt || {},
            cashReceipt: result.data.cash_receipt || {},
          });
        }
      } catch (fetchError) {
        console.error('환불 정보 조회 실패:', fetchError);
        if (!cancelled) setError(fetchError.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRefundData();
    document.title = '환불 영수증 - 왓퀴즈';

    return () => {
      cancelled = true;
    };
  }, [currentUser, navigate, paymentKey, location.state]);

  const handleOpenOfficialReceipt = () => {
    const receiptUrl = refundData?.receipt?.url;
    if (!receiptUrl) return;

    if (receiptUrl.includes('ref=test')) {
      showToast('테스트 환경에서는 토스 공식 영수증이 제공되지 않아요', 'info');
      return;
    }

    const receiptWindow = window.open(receiptUrl, '_blank', 'noopener,noreferrer');
    if (receiptWindow) receiptWindow.opener = null;
  };

  if (loading) {
    return (
      <PaymentFlowStatus
        variant="processing"
        title="환불 내역을 불러오고 있어요"
        description="환불된 금액과 처리 시점을 확인하고 있어요."
      />
    );
  }

  if (error) {
    return (
      <PaymentFlowStatus
        variant="error"
        title="환불 내역을 불러오지 못했어요"
        description="잠시 후 다시 시도하거나 구독 관리에서 환불 내역을 확인해주세요."
        detailCode={error}
        primaryAction={{
          label: '구독 관리로 돌아가기',
          onClick: () => navigate('/subscription'),
        }}
      />
    );
  }

  if (!refundData) {
    return (
      <PaymentFlowStatus
        variant="error"
        title="환불 영수증을 찾지 못했어요"
        description="구독 관리에서 환불 내역을 다시 확인해주세요."
        primaryAction={{
          label: '구독 관리로 돌아가기',
          onClick: () => navigate('/subscription'),
        }}
      />
    );
  }

  if (
    refundData.refundReconciliationRequired
    || refundData.refund_reconciliation_required
  ) {
    return (
      <PaymentFlowStatus
        variant="processing"
        title="부분 취소 내역을 확인하고 있어요"
        description="결제 취소 금액과 PRO 이용 기간을 확인한 뒤 환불 영수증을 보여드릴게요."
        primaryAction={{
          label: '구독 관리로 돌아가기',
          onClick: () => navigate('/subscription'),
        }}
      />
    );
  }

  const refundAmount = Number(refundData.refundAmount ?? refundData.amount) || 0;
  const originalAmount = Number(refundData.amount) || 0;
  const retainedAmount = Number(refundData.retainedAmount) || 0;
  const isProrated = refundData.refundType === 'prorated';
  const refundCalculation = refundData.refundCalculation || {};
  const refundImpact = refundData.refundImpact || {};
  const referenceNumber = refundData.receiptNumber
    || refundData.paymentKey
    || refundData.orderId
    || '-';
  const hasOfficialReceipt = Boolean(refundData?.receipt?.url);

  return (
    <main className="receiptpage-page">
      <div className="receiptpage-container">
        <div className="receiptpage-toolbar">
          <button
            type="button"
            className="receiptpage-button receiptpage-button--ghost"
            onClick={() => navigate('/subscription')}
          >
            구독 관리
          </button>
          {hasOfficialReceipt && (
            <button
              type="button"
              className="receiptpage-button receiptpage-button--secondary"
              onClick={handleOpenOfficialReceipt}
            >
              공식 영수증 열기
            </button>
          )}
        </div>

        <header className="receiptpage-refund-hero">
          <div className="receiptpage-refund-icon">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M20 6L9 17L4 12"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h1>{isProrated ? '일할 환불을 완료했어요' : '환불을 완료했어요'}</h1>
          <p>
            <strong>{refundAmount.toLocaleString()}원</strong>을 원래 결제 수단으로 돌려드려요
          </p>
        </header>

        <article className="receiptpage-document">
          <header className="receiptpage-header">
            <p className="receiptpage-brand">왓퀴즈</p>
            <h2 className="receiptpage-title">환불 영수증</h2>
            <span className="receiptpage-status-badge">
              {isProrated ? '일할 환불 완료' : '환불 완료'}
            </span>
          </header>

          <div className="receiptpage-reference">
            <span className="receiptpage-reference-label">환불 참조번호</span>
            <span className="receiptpage-reference-value">{referenceNumber}</span>
          </div>

          <section className="receiptpage-section">
            <h3 className="receiptpage-section-title">환불 내역</h3>
            <div className="receiptpage-row">
              <span className="receiptpage-label">환불일시</span>
              <span className="receiptpage-value">{formatReceiptDate(refundData.refundedAt)}</span>
            </div>
            <div className="receiptpage-row">
              <span className="receiptpage-label">환불금액</span>
              <strong className="receiptpage-total-amount">
                {refundAmount.toLocaleString()}원
              </strong>
            </div>
            <div className="receiptpage-row">
              <span className="receiptpage-label">환불수단</span>
              <span className="receiptpage-value">
                {getRefundMethodName(refundData.paymentMethod)}
              </span>
            </div>
            <div className="receiptpage-row">
              <span className="receiptpage-label">환불사유</span>
              <span className="receiptpage-value">
                {refundData.cancelReason || '고객 요청'}
              </span>
            </div>
          </section>

          {isProrated && (
            <section className="receiptpage-section">
              <h3 className="receiptpage-section-title">일할 계산 내역</h3>
              <div className="receiptpage-row">
                <span className="receiptpage-label">계산 기간</span>
                <span className="receiptpage-value">
                  {refundCalculation.payment_date || '-'}
                  {' ~ '}
                  {refundCalculation.cutoff_date || '-'}
                </span>
              </div>
              <div className="receiptpage-row">
                <span className="receiptpage-label">전체 이용기간</span>
                <span className="receiptpage-value">
                  {Number(refundCalculation.total_days || 0).toLocaleString()}일
                </span>
              </div>
              <div className="receiptpage-row">
                <span className="receiptpage-label">사용기간</span>
                <span className="receiptpage-value">
                  {Number(refundCalculation.used_days || 0).toLocaleString()}일
                </span>
              </div>
              <div className="receiptpage-row">
                <span className="receiptpage-label">남은 기간</span>
                <span className="receiptpage-value">
                  {Number(refundCalculation.unused_days || 0).toLocaleString()}일
                </span>
              </div>
              <div className="receiptpage-row">
                <span className="receiptpage-label">사용 금액</span>
                <span className="receiptpage-value">
                  {retainedAmount.toLocaleString()}원
                </span>
              </div>
            </section>
          )}

          <section className="receiptpage-section">
            <h3 className="receiptpage-section-title">원결제 내역</h3>
            <div className="receiptpage-row">
              <span className="receiptpage-label">이용권</span>
              <span className="receiptpage-value">
                {refundData.planName || '왓퀴즈 PRO 이용권'}
              </span>
            </div>
            <div className="receiptpage-row">
              <span className="receiptpage-label">원결제 금액</span>
              <span className="receiptpage-value">{originalAmount.toLocaleString()}원</span>
            </div>
            <div className="receiptpage-row">
              <span className="receiptpage-label">주문번호</span>
              <span className="receiptpage-value receiptpage-value--mono">
                {refundData.orderId || '-'}
              </span>
            </div>
            <div className="receiptpage-row">
              <span className="receiptpage-label">원결제일시</span>
              <span className="receiptpage-value">
                {formatReceiptDate(refundData.originalPaymentDate)}
              </span>
            </div>
          </section>
        </article>

        <section className="receiptpage-notice">
          <h2 className="receiptpage-notice-title">환불금은 이렇게 들어와요</h2>
          <ul>
            <li>결제 수단에 따라 취소 내역 반영까지 3~5영업일이 걸릴 수 있어요.</li>
            <li>정확한 반영 시점은 결제한 카드사나 결제 서비스에서 확인할 수 있어요.</li>
            <li>
              {refundImpact.subscription_remains_active
                ? `${formatReceiptDay(refundImpact.next_end_date)}까지 PRO를 이용할 수 있어요.`
                : '환불 완료 시점부터 PRO 기능 이용이 종료돼요.'}
            </li>
          </ul>
        </section>

        <div className="receiptpage-actions">
          <button
            type="button"
            className="receiptpage-button receiptpage-button--primary"
            onClick={() => navigate('/subscription')}
          >
            구독 관리로 돌아가기
          </button>
          <button
            type="button"
            className="receiptpage-button receiptpage-button--secondary"
            onClick={() => navigate('/home')}
          >
            홈으로 이동하기
          </button>
        </div>

        <p className="receiptpage-support">
          환불 관련 도움이 필요하면{' '}
          <a href="mailto:angel@plactical.com?subject=환불 관련 문의">
            angel@plactical.com
          </a>으로 연락해주세요
        </p>
      </div>
    </main>
  );
};

export default RefundReceipt;
